/**
 * Wire contracts for the account-deletion callables (deployed from the
 * isolated codebase `src/apps/functions-isolated/account-deletion`),
 * imported by both that codebase and the webapp client.
 *
 * Tracking: https://github.com/CodeinPlace/cip/issues/3263
 */
import {z} from 'zod';

import {
  DeletionJobHealthSchema,
  DeletionTimelineEntrySchema,
} from './consoleShapes';
import {
  AccountDeletionSourceSchema,
  AccountDeletionStatusSchema,
} from './tombstone';

/**
 * Deployed name of the callable that starts a deletion. Lowercase, per
 * the repo's function-name convention.
 */
export const START_ACCOUNT_DELETION_JOB_CALLABLE =
  'startaccountdeletionjob' as const;

/**
 * Deployed name of the admin-only status callable. Named for the
 * tombstone it reads, not the job docs — callers address a uid or email
 * and never see a job id. See {@link GetAccountDeletionStatusResponse}.
 */
export const GET_ACCOUNT_DELETION_STATUS_CALLABLE =
  'getaccountdeletionstatus' as const;

/**
 * How an admin identifies the account to act on: exactly one of uid or
 * email. Email is resolved server-side to a uid; an ambiguous or missing
 * match is an error, never a guess.
 *
 * The uid must be usable as a single Firestore document id, within Firebase
 * Auth's uid length limit. A pasted `abc123/` would otherwise resolve to
 * `users/abc123` (the SDK drops empty path segments) and run the cascade
 * against a uid no query matches, reporting it complete while the real
 * account stayed enabled. Both shapes are strict, so a request naming a
 * uid and an email is rejected rather than silently using the uid.
 */
export const AccountDeletionTargetSchema = z.union([
  z.strictObject({
    uid: z
      .string()
      .min(1)
      .max(128)
      .regex(
        /^(?!\.\.?$)[^/]+$/,
        'A uid cannot contain "/" or be "." or "..".',
      ),
  }),
  z.strictObject({email: z.email()}),
]);

/** Admin-supplied account identifier. See {@link AccountDeletionTargetSchema}. */
export type AccountDeletionTarget = z.infer<typeof AccountDeletionTargetSchema>;

/**
 * Request for `startaccountdeletionjob`.
 *
 * - Self-service: omit `target` — the callable acts on the caller's own
 *   account (`request.auth.uid`).
 * - Admin mode: supply `target` — the caller must be a superadmin; the
 *   cascade runs against the targeted account.
 *
 * When `dryRun` is omitted the deletion runs for real; `dryRun: true`
 * enumerates and reports without writing (admin mode only — a
 * self-service dry run would leave the account disabled with nothing
 * deleted).
 *
 * A real deletion defaults to **deferred**: the account is locked out at
 * once and the destructive cascade is left to the next deletion wave
 * (see {@link AccountDeletionStatusSchema}'s `scheduled` status).
 * `runNow: true` opts out of the wait and starts the cascade
 * immediately — admin mode only, for legal demands and abuse accounts.
 */
export const StartAccountDeletionJobRequestSchema = z.object({
  target: AccountDeletionTargetSchema.optional(),
  dryRun: z.boolean().optional(),
  /**
   * Superadmin-only escape hatch: start the cascade now instead of waiting
   * for the wave. Requires `target` (admin mode); omitted or false means
   * the deletion is deferred to the wave. Ignored for `dryRun`, which
   * never touches a tombstone.
   *
   * On an account that already has a tombstone, run-now dispatches a
   * fresh job when there is something left to run: a `scheduled`
   * deletion is brought forward, a `failed` or `needs-attention` one is
   * re-run (tasks already checkpointed are skipped), and an `in-progress`
   * one only when its dispatch looks lost. Otherwise the response attaches
   * to the existing deletion.
   */
  runNow: z.boolean().optional(),
  /**
   * With `runNow`, on a re-run: forget every task checkpoint so the whole
   * cascade runs again, instead of only the tasks that never completed.
   * Every task is idempotent, so the cost is time, not data. For an
   * account whose task completed under code that has since been fixed:
   * the checkpoint would otherwise keep the fix from ever reaching it.
   * Rejected without `runNow` or outside admin mode.
   */
  fromScratch: z.boolean().optional(),
});

/** Request for `startaccountdeletionjob`. */
export type StartAccountDeletionJobRequest = z.infer<
  typeof StartAccountDeletionJobRequestSchema
>;

/**
 * Response from `startaccountdeletionjob`. When a deletion is already in
 * flight for the target, the existing job/tombstone state is returned
 * (`alreadyInProgress: true`) instead of erroring — the callable is
 * idempotent.
 */
export interface StartAccountDeletionJobResponse {
  /**
   * Id of the job doc executing (or about to execute) the cascade. The
   * empty string when the cascade was deferred to a wave: no
   * job doc exists yet, and the one the wave creates is not knowable
   * here. Also empty when an already-`scheduled` tombstone was attached
   * to.
   */
  jobId: string;
  /** Uid the cascade targets (resolved from email in admin mode). */
  targetUid: string;
  /** Tombstone status after this call. */
  status: z.infer<typeof AccountDeletionStatusSchema>;
  /** True when this call attached to an existing in-flight deletion. */
  alreadyInProgress: boolean;
  /**
   * Epoch millis of the earliest deletion wave this account is eligible
   * for, when the cascade was deferred (`status: 'scheduled'`).
   * Timestamps do not survive the callable JSON boundary, hence millis.
   * Null when the cascade started immediately, and null for a dry run.
   *
   * Advisory: the orchestrator's over-age backstop can bring an account
   * forward, so treat this as "not before", not "exactly then".
   */
  scheduledForMs: number | null;
}

/** Request for `getaccountdeletionstatus` (admin-only). */
export const GetAccountDeletionStatusRequestSchema = z.object({
  target: AccountDeletionTargetSchema,
});

/** Request for `getaccountdeletionstatus`. */
export type GetAccountDeletionStatusRequest = z.infer<
  typeof GetAccountDeletionStatusRequestSchema
>;

/**
 * Response from `getaccountdeletionstatus`: a PII-free snapshot of the
 * tombstone for admin status display, and the case detail the staff
 * console shows for it. `null` when the target has no tombstone (no
 * deletion was ever requested). Strict, and parsed by the server before it
 * returns, so audit `detail` text, `requestedBy` and job exception text
 * cannot ride along.
 */
export const GetAccountDeletionStatusResponseSchema = z.strictObject({
  tombstone: z
    .strictObject({
      /** The account's uid; the caller supplied it or its email. */
      uid: z.string().min(1),
      status: AccountDeletionStatusSchema,
      source: AccountDeletionSourceSchema,
      /** Epoch millis — Timestamps do not survive the callable JSON boundary. */
      requestedAtMs: z.number(),
      /** Epoch millis, or null while the cascade is still running. */
      completedAtMs: z.number().nullable(),
      /** Manifest task ids already completed (resume checkpoints). */
      completedTaskIds: z.array(z.string()),
      /** Manifest task ids not yet completed. */
      outstandingTaskIds: z.array(z.string()),
      /** Total tasks in the manifest, for progress display. */
      totalTasks: z.number().int().positive(),
      /**
       * Epoch millis of the earliest deletion wave this account is
       * eligible for; null unless the cascade is deferred (`scheduled`).
       * Lets staff see how long a pending deletion still has to wait.
       */
      scheduledForMs: z.number().nullable(),
      /** Whether a legal hold is in force. */
      legalHold: z.boolean(),
      /** The active job's health; null when no job is named. */
      job: DeletionJobHealthSchema.nullable(),
      /** Audit events, oldest first: action and task only, no text. */
      timeline: z.array(DeletionTimelineEntrySchema),
    })
    .nullable(),
});

/** See {@link GetAccountDeletionStatusResponseSchema}. */
export type GetAccountDeletionStatusResponse = z.infer<
  typeof GetAccountDeletionStatusResponseSchema
>;

/**
 * Deployed name of the superadmin-only callable that cancels a `scheduled`
 * deletion and re-enables the account.
 */
export const CANCEL_ACCOUNT_DELETION_CALLABLE =
  'cancelaccountdeletion' as const;

/** Request for `cancelaccountdeletion`: the account whose deletion to cancel. */
export const CancelAccountDeletionRequestSchema = z.strictObject({
  target: AccountDeletionTargetSchema,
});

/** Request for `cancelaccountdeletion`. */
export type CancelAccountDeletionRequest = z.infer<
  typeof CancelAccountDeletionRequestSchema
>;

/**
 * Response from `cancelaccountdeletion`. Calling again on an
 * already-cancelled deletion retries the re-enable and reports
 * `alreadyCancelled: true`, so a call that failed after cancelling can be
 * retried.
 */
export const CancelAccountDeletionResponseSchema = z.strictObject({
  /** The uid whose deletion was cancelled. */
  targetUid: z.string().min(1),
  /** True when the deletion was already cancelled before this call. */
  alreadyCancelled: z.boolean(),
});

/** See {@link CancelAccountDeletionResponseSchema}. */
export type CancelAccountDeletionResponse = z.infer<
  typeof CancelAccountDeletionResponseSchema
>;
